import { ErrorHandler, Injectable, Injector, NgZone, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

/** Timestamp of the last automatic recovery reload, scoped to the browser tab. */
const RELOAD_MARKER_KEY = 'oneportfolio.chunk-reload-at';

/**
 * A second chunk failure inside this window means the reload did not fix anything,
 * so recovery stops and the user is informed instead of reloading again.
 */
const RELOAD_COOLDOWN_MS = 30_000;

const REFRESH_REQUIRED_KEY = 'MESSAGES.ERROR.APP_UPDATE_REFRESH_REQUIRED';
const REFRESH_REQUIRED_FALLBACK =
  'A new version of the application is available. Please refresh the page.';

const CHUNK_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'loading chunk',
  'loading css chunk',
  'unable to preload css',
];

function collectMessages(error: unknown, depth = 0): string {
  if (!error || depth > 3) return '';
  if (typeof error === 'string') return error;

  const candidate = error as { message?: unknown; rejection?: unknown; cause?: unknown };
  return [
    typeof candidate.message === 'string' ? candidate.message : '',
    collectMessages(candidate.rejection, depth + 1),
    collectMessages(candidate.cause, depth + 1),
  ].join(' ');
}

export function isChunkLoadError(error: unknown): boolean {
  const message = collectMessages(error).toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Recovers from lazy-chunk load failures that happen when a new build is deployed
 * while a tab still runs the previous one: the hashed chunk referenced by the loaded
 * app no longer exists on the server, so a single full reload picks up the new build.
 *
 * Recovery is attempted at most once per cooldown window, so a chunk that stays
 * unreachable (server down, offline, blocked storage) can never cause a reload loop.
 */
@Injectable()
export class ChunkLoadErrorHandler extends ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly zone = inject(NgZone);
  private notified = false;

  override handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      this.notifyRefreshRequired();

      if (this.reloadOnce()) {
        return;
      }
    }

    super.handleError(error);
  }

  /** Seam for tests: performing the actual document reload. */
  protected reloadPage(): void {
    location.reload();
  }

  private reloadOnce(): boolean {
    if (!navigator.onLine) {
      return false;
    }

    const lastAttempt = this.readLastAttempt();
    if (lastAttempt === null) {
      // Session storage is unavailable, so a reload could not be tracked and might loop.
      return false;
    }

    const now = Date.now();
    if (now - lastAttempt < RELOAD_COOLDOWN_MS) {
      return false;
    }

    if (!this.writeLastAttempt(now)) {
      return false;
    }

    this.zone.runOutsideAngular(() => this.reloadPage());
    return true;
  }

  private readLastAttempt(): number | null {
    try {
      return Number(sessionStorage.getItem(RELOAD_MARKER_KEY)) || 0;
    } catch {
      return null;
    }
  }

  private writeLastAttempt(timestamp: number): boolean {
    try {
      sessionStorage.setItem(RELOAD_MARKER_KEY, String(timestamp));
      return true;
    } catch {
      return false;
    }
  }

  private notifyRefreshRequired(): void {
    if (this.notified) {
      return;
    }
    this.notified = true;

    try {
      const toastr = this.injector.get(ToastrService, null);
      if (!toastr) {
        return;
      }

      const translate = this.injector.get(TranslateService, null);
      const translated = translate?.instant(REFRESH_REQUIRED_KEY);
      const message =
        translated && translated !== REFRESH_REQUIRED_KEY ? translated : REFRESH_REQUIRED_FALLBACK;

      this.zone.run(() => toastr.error(message, undefined, { disableTimeOut: true }));
    } catch {
      // Never let error reporting throw from inside the error handler.
    }
  }
}
